import { Flip, KeyboardArrowDown, KeyboardArrowUp, PersonRemoveOutlined } from "@mui/icons-material";
import { Box, IconButton, styled, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { DateTime } from "luxon";
import { Fragment, forwardRef, useCallback, useState } from "react";
import type { Nurse, NurseInShift } from "../../../utils/models";
import { isAgencyNurse } from "../../../utils/warningDialogs/WarningDialog";
import { DAILY_DOT_ITEMS, useDailyShiftBreakdown } from "../hooks/useDailyShiftBreakdown";
import type { SimulationEntry } from "../hooks/useHiringData";
import { useSimulationAnimation } from "../hooks/useSimulationAnimation";
import { COLORS, HiringShiftType, isDayShift, matchesRoleGroup, ROLE_GROUPS } from "../types";
import type { MissingRecommendShiftFilter } from "./HiringMissingRecommendDialog";
import { HiringMissingRecommendDialog } from "./HiringMissingRecommendDialog";
import { SimulationCell } from "./SimulationCell";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
	padding: "8px 12px",
	fontSize: "14px",
	fontWeight: 500,
	color: theme.palette.primary.dark,
	borderBottom: "1px solid #E1E6EF",
	borderRight: "1px solid #F4F7FE",
	"&.MuiTableCell-head": {
		backgroundColor: alpha(theme.palette.primary.main, 0.08),
		color: theme.palette.primary.dark,
		fontWeight: 600,
		whiteSpace: "nowrap",
		position: "sticky",
		top: 0,
		zIndex: 3,
	},
}));

const StyledTableRow = styled(TableRow)(() => ({
	"&:hover": {
		backgroundColor: "#F8F9FC",
	},
}));

interface HiringStaffingTableProps {
	dates: DateTime[];
	calculateNeeded: (role: string) => { day: number; night: number; total: number };
	getShiftsForDate: (date: DateTime) => NurseInShift[];
	nurses: Nurse[];
	simulationData: SimulationEntry[];
}

/** Unique simulation count for a role on a date by shift type (day vs night). */
const getSimulationCountByShift = (simulationData: SimulationEntry[], role: string, date: DateTime, isDay: boolean): number => {
	const seen = new Set<string>();
	const shiftType = isDay ? HiringShiftType.Day : HiringShiftType.Night;
	for (const s of simulationData.filter((s) => s.role === role && s.date.hasSame(date, "day") && s.shiftType === shiftType)) {
		seen.add(s.nurseName);
	}
	return seen.size;
};

export const HiringStaffingTable = forwardRef<HTMLDivElement, HiringStaffingTableProps>(
	({ dates, calculateNeeded, getShiftsForDate, nurses, simulationData }, ref) => {
		const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
		const [actualCellMode, setActualCellMode] = useState<Record<string, boolean>>({});
		const [recommendationAnchor, setRecommendationAnchor] = useState<{
			role: string;
			date: DateTime;
			shiftType: MissingRecommendShiftFilter;
		} | null>(null);
		const freshKeys = useSimulationAnimation(simulationData);
		const dailyStatsByRoleDate = useDailyShiftBreakdown({ dates, getShiftsForDate, nurses });

		const getCalculatedActuals = useCallback(
			(
				roleKey: string,
				date: DateTime,
				neededForRole: { day: number; night: number },
			): {
				dailyStats: { recurring: number; pickup: number; overtime: number; agency: number };
				coreActual: number;
				actualDay: number;
				actualNight: number;
			} => {
				const dateKey = date.toISODate() || "";
				const dailyStats = dailyStatsByRoleDate[roleKey]?.[dateKey] ?? {
					recurring: 0,
					pickup: 0,
					overtime: 0,
					agency: 0,
				};

				const coreActual = dailyStats.recurring + dailyStats.pickup;
				const totalNeededForRole = neededForRole.day + neededForRole.night || 1;
				const dayShare = neededForRole.day / totalNeededForRole;
				const actualDay = Math.round(coreActual * dayShare);
				const actualNight = coreActual - actualDay;

				return { dailyStats, coreActual, actualDay, actualNight };
			},
			[dailyStatsByRoleDate],
		);

		const toggleActualCell = useCallback((role: string, date: DateTime) => {
			const key = `${role}-${date.toISODate()}`;
			setActualCellMode((prev) => ({
				...prev,
				[key]: !prev[key],
			}));
		}, []);

		// Core shifts = recurring + pickup (exclude agency and overtime).
		const isCoreShift = useCallback(
			(shift: NurseInShift): boolean => {
				const nurse = nurses.find((n) => n.id === shift.nurseId);
				const isAgency = !!(nurse?.agencyName || (nurse && isAgencyNurse(nurse.employmentType)));
				if (isAgency) return false;
				if (shift.bonus > 0) return false; // overtime
				return true;
			},
			[nurses],
		);

		const getShiftColor = useCallback((actual: number, needed: number): string => {
			if (actual === needed || needed === 0) return COLORS.success;
			const diff = Math.abs(actual - needed);
			if (diff / needed > 0.2) return COLORS.error;
			return COLORS.warning;
		}, []);

		const getNurseNames = (shifts: NurseInShift[]): string => shifts.map((shift) => shift.name).join("\n");

		const toggleRow = (role: string, type: "needed" | "actual" | "simulation" | "missing") => {
			const key = `${role}-${type}`;
			setExpandedRows((prev) => ({ ...prev, [key]: !prev[key] }));
		};

		return (
			<>
				<TableContainer ref={ref} data-testid="hiring-staffing-table" sx={{ height: "100%", border: "1px solid #E1E6EF", borderRadius: "8px" }}>
					<Table size="small" stickyHeader>
						<TableHead>
							<TableRow>
								<StyledTableCell sx={{ minWidth: 150, textAlign: "center", fontWeight: 700 }}>Role Group</StyledTableCell>
								<StyledTableCell sx={{ fontWeight: 700 }}>Shifts</StyledTableCell>
								{dates.map((date, i) => (
									<StyledTableCell
										key={date.toISODate()}
										align="center"
										sx={{
											borderLeft: i === 7 ? (theme) => `2px solid ${theme.palette.primary.main}` : "none",
											minWidth: 60,
										}}
									>
										<Typography sx={{ fontSize: "12px", fontWeight: 600 }}>{date.toFormat("ccc")}</Typography>
									</StyledTableCell>
								))}
							</TableRow>
						</TableHead>
						<TableBody>
							{ROLE_GROUPS.map((role) => {
								const needed = calculateNeeded(role);
								const neededExpanded = expandedRows[`${role}-needed`] ? 2 : 0;
								const actualExpanded = expandedRows[`${role}-actual`] ? 2 : 0;
								const hasSimData = simulationData.some((s) => s.role === role);
								const simExpanded = expandedRows[`${role}-simulation`] ? 2 : 0;
								// 5 base rows: Needed, Actual, Simulation, Missing Day, Missing Night
								const totalRows = 5 + neededExpanded + actualExpanded + (hasSimData ? simExpanded : 0);
								// Per-role animation counter — resets for each role so they animate independently
								const roleAnimCounter = { count: 0 };
								return (
									<Fragment key={role}>
										<StyledTableRow>
											<StyledTableCell
												rowSpan={totalRows}
												sx={{
													fontWeight: 900,
													verticalAlign: "middle",
													textAlign: "center",
													fontSize: "24px",
													color: "primary.dark",
													backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
													borderRight: "2px solid #D1D9ED",
													borderBottom: "2px solid #D1D9ED",
													letterSpacing: "1px",
													width: "160px",
												}}
											>
												{role}
											</StyledTableCell>
											<StyledTableCell sx={{ color: "primary.main", fontWeight: 600 }}>
												<IconButton data-testid={`hiring-staffing-${role}-needed-toggle`} size="small" onClick={() => toggleRow(role, "needed")} sx={{ mr: 1 }}>
													{expandedRows[`${role}-needed`] ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
												</IconButton>
												Needed
											</StyledTableCell>
											{dates.map((date, i) => (
												<StyledTableCell
													key={`needed-total-${date.toISODate()}`}
													align="center"
													sx={{ borderLeft: i === 7 ? (theme) => `2px solid ${theme.palette.primary.main}` : "none", fontWeight: 700 }}
												>
													{needed.total}
												</StyledTableCell>
											))}
										</StyledTableRow>
										{expandedRows[`${role}-needed`] && (
											<>
												<StyledTableRow sx={{ backgroundColor: "#F8F9FC" }}>
													<StyledTableCell sx={{ pl: 5, color: "primary.light", fontSize: "13px" }}>Day</StyledTableCell>
													{dates.map((date, i) => (
														<StyledTableCell
															key={`needed-day-${date.toISODate()}`}
															align="center"
															sx={{ borderLeft: i === 7 ? (theme) => `2px solid ${theme.palette.primary.main}` : "none", fontWeight: 600 }}
														>
															{needed.day}
														</StyledTableCell>
													))}
												</StyledTableRow>
												<StyledTableRow sx={{ backgroundColor: "#F8F9FC" }}>
													<StyledTableCell sx={{ pl: 5, color: "primary.light", fontSize: "13px" }}>Night</StyledTableCell>
													{dates.map((date, i) => (
														<StyledTableCell
															key={`needed-night-${date.toISODate()}`}
															align="center"
															sx={{ borderLeft: i === 7 ? (theme) => `2px solid ${theme.palette.primary.main}` : "none", fontWeight: 600 }}
														>
															{needed.night}
														</StyledTableCell>
													))}
												</StyledTableRow>
											</>
										)}

										<StyledTableRow>
											<StyledTableCell
												sx={{
													color: "primary.main",
													fontWeight: 600,
												}}
											>
												<IconButton data-testid={`hiring-staffing-${role}-actual-toggle`} size="small" onClick={() => toggleRow(role, "actual")} sx={{ mr: 1 }}>
													{expandedRows[`${role}-actual`] ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
												</IconButton>
												Actual
											</StyledTableCell>
											{dates.map((date, i) => {
												// Tooltip names: only show recurring core staff, not pickup/agency/OT
												const recurringShifts = getShiftsForDate(date).filter((s) => {
													if (!matchesRoleGroup(s.role, role)) return false;
													const nurse = nurses.find((n) => n.id === s.nurseId);
													const isAgency = !!(nurse?.agencyName || (nurse && isAgencyNurse(nurse.employmentType)));
													if (isAgency) return false;
													if (s.requestId) return false;
													if (s.bonus > 0) return false;
													return true;
												});
												const dateKey = date.toISODate() || "";
												const cellKey = `${role}-${dateKey}`;
												const showBreakdown = !!actualCellMode[cellKey];

												const { dailyStats, coreActual } = getCalculatedActuals(role, date, needed);

												return (
													<Tooltip
														key={`${role}-actual-total-${date.toISODate()}`}
														title={getNurseNames(recurringShifts) || "No nurses"}
														placement="top"
														arrow
														slotProps={{ tooltip: { sx: { whiteSpace: "pre-line" } } }}
													>
														<StyledTableCell
															align="center"
															sx={{
																borderLeft: i === 7 ? (theme) => `2px solid ${theme.palette.primary.main}` : "none",
																fontWeight: 800,
																fontSize: "15px",
																cursor: "pointer",
															}}
															onClick={() => toggleActualCell(role, date)}
														>
															{showBreakdown ? (
																<Box
																	sx={{
																		display: "grid",
																		gridTemplateColumns: "1fr 1fr",
																		gap: 0.25,
																		justifyItems: "center",
																	}}
																>
																	{DAILY_DOT_ITEMS.map((item) => (
																		<Box
																			key={item.key}
																			sx={{
																				display: "flex",
																				alignItems: "center",
																				justifyContent: "center",
																				gap: 0.5,
																			}}
																		>
																			<Box
																				sx={{
																					width: 2,
																					height: 8,
																					bgcolor: item.color,
																				}}
																			/>
																			<Typography sx={{ fontSize: "10px", color: "primary.dark", fontWeight: 700 }}>
																				{item.key[0].toUpperCase()}: {dailyStats[item.key]}
																			</Typography>
																		</Box>
																	))}
																</Box>
															) : (
																coreActual
															)}
														</StyledTableCell>
													</Tooltip>
												);
											})}
										</StyledTableRow>
										{expandedRows[`${role}-actual`] && (
											<>
												<StyledTableRow sx={{ backgroundColor: "#F8F9FC" }}>
													<StyledTableCell sx={{ pl: 5, color: "primary.light", fontSize: "13px" }}>Day</StyledTableCell>
													{dates.map((date, i) => {
														const shifts = getShiftsForDate(date).filter((s) => matchesRoleGroup(s.role, role) && isDayShift(s.shiftType));
														const { actualDay } = getCalculatedActuals(role, date, needed);

														return (
															<Tooltip
																key={`day-${date.toISODate()}`}
																title={getNurseNames(shifts) || "No nurses"}
																placement="top"
																arrow
																slotProps={{ tooltip: { sx: { whiteSpace: "pre-line" } } }}
															>
																<StyledTableCell
																	align="center"
																	sx={{
																		borderLeft: i === 7 ? (theme) => `2px solid ${theme.palette.primary.main}` : "none",
																		fontWeight: 700,
																		cursor: "default",
																	}}
																>
																	{actualDay}
																</StyledTableCell>
															</Tooltip>
														);
													})}
												</StyledTableRow>
												<StyledTableRow sx={{ backgroundColor: "#F8F9FC" }}>
													<StyledTableCell sx={{ pl: 5, color: "primary.light", fontSize: "13px" }}>Night</StyledTableCell>
													{dates.map((date, i) => {
														const shifts = getShiftsForDate(date).filter((s) => matchesRoleGroup(s.role, role) && !isDayShift(s.shiftType));
														const { actualNight } = getCalculatedActuals(role, date, needed);

														return (
															<Tooltip
																key={`night-${date.toISODate()}`}
																title={getNurseNames(shifts) || "No nurses"}
																placement="top"
																arrow
																slotProps={{ tooltip: { sx: { whiteSpace: "pre-line" } } }}
															>
																<StyledTableCell
																	align="center"
																	sx={{
																		borderLeft: i === 7 ? (theme) => `2px solid ${theme.palette.primary.main}` : "none",
																		fontWeight: 700,
																		cursor: "default",
																	}}
																>
																	{actualNight}
																</StyledTableCell>
															</Tooltip>
														);
													})}
												</StyledTableRow>
											</>
										)}

										{/* Simulation row */}
										<StyledTableRow>
											<StyledTableCell
												sx={{
													color: hasSimData ? "primary.main" : "primary.light",
													fontWeight: 600,
												}}
											>
												{hasSimData ? (
													<>
														<IconButton
															data-testid={`hiring-staffing-${role}-simulation-toggle`}
															size="small"
															onClick={() => toggleRow(role, "simulation")}
															sx={{ mr: 1 }}
														>
															{expandedRows[`${role}-simulation`] ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
														</IconButton>
														Simulation
													</>
												) : (
													"Simulation"
												)}
											</StyledTableCell>
											{dates.map((date, i) => (
												<SimulationCell
													key={`sim-${date.toISODate()}`}
													entries={simulationData.filter((s) => s.role === role && s.date.hasSame(date, "day"))}
													date={date}
													columnIndex={i}
													freshKeys={freshKeys}
													roleAnimCounter={roleAnimCounter}
												/>
											))}
										</StyledTableRow>

										{/* Simulation Day/Night sub-rows */}
										{hasSimData && expandedRows[`${role}-simulation`] && (
											<>
												<StyledTableRow sx={{ backgroundColor: "#F8F9FC" }}>
													<StyledTableCell sx={{ pl: 5, color: "primary.light", fontSize: "13px" }}>Day</StyledTableCell>
													{dates.map((date, i) => (
														<SimulationCell
															key={`sim-day-${date.toISODate()}`}
															entries={simulationData.filter((s) => s.role === role && s.date.hasSame(date, "day") && s.shiftType === HiringShiftType.Day)}
															date={date}
															columnIndex={i}
															freshKeys={freshKeys}
															roleAnimCounter={roleAnimCounter}
															ignoreSimulation
														/>
													))}
												</StyledTableRow>
												<StyledTableRow sx={{ backgroundColor: "#F8F9FC" }}>
													<StyledTableCell sx={{ pl: 5, color: "primary.light", fontSize: "13px" }}>Night</StyledTableCell>
													{dates.map((date, i) => (
														<SimulationCell
															key={`sim-night-${date.toISODate()}`}
															entries={simulationData.filter((s) => s.role === role && s.date.hasSame(date, "day") && s.shiftType === HiringShiftType.Night)}
															date={date}
															columnIndex={i}
															freshKeys={freshKeys}
															roleAnimCounter={roleAnimCounter}
															ignoreSimulation
														/>
													))}
												</StyledTableRow>
											</>
										)}

										{/* Missing rows */}
										<StyledTableRow sx={{ backgroundColor: "#F8F9FC" }}>
											<StyledTableCell sx={{ pl: 5, color: "primary.light", fontSize: "13px" }}>Missing Day</StyledTableCell>
											{dates.map((date, i) => {
												const dateKey = date.toISODate() || "";
												const dailyStats = dailyStatsByRoleDate[role]?.[dateKey] ?? {
													recurring: 0,
													pickup: 0,
													overtime: 0,
													agency: 0,
												};

												const coreActual = dailyStats.recurring + dailyStats.pickup;
												const totalNeededForRole = needed.day + needed.night || 1;
												const dayShare = needed.day / totalNeededForRole;
												const actualDayCalc = Math.round(coreActual * dayShare);

												const simDay = getSimulationCountByShift(simulationData, role, date, true);
												const totalDay = actualDayCalc + simDay;
												const missingDay = needed.day - totalDay;
												const color = getShiftColor(totalDay, needed.day);
												const isNegative = missingDay < 0;
												return (
													<StyledTableCell
														key={`missing-day-${date.toISODate()}`}
														align="center"
														onClick={() => setRecommendationAnchor({ role, date, shiftType: "day" })}
														sx={{
															borderLeft: i === 7 ? (theme) => `2px solid ${theme.palette.primary.main}` : "none",
															color,
															fontWeight: 700,
															cursor: "pointer",
														}}
													>
														{isNegative ? (
															<Tooltip title="Surplus - consider moving staff" arrow>
																<Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
																	{missingDay}
																	<PersonRemoveOutlined sx={{ fontSize: 14, color: COLORS.warning }} />
																</Box>
															</Tooltip>
														) : (
															missingDay
														)}
													</StyledTableCell>
												);
											})}
										</StyledTableRow>
										<StyledTableRow sx={{ backgroundColor: "#F8F9FC" }}>
											<StyledTableCell sx={{ pl: 5, color: "primary.light", fontSize: "13px", borderBottom: "2px solid #D1D9ED" }}>
												Missing Night
											</StyledTableCell>
											{dates.map((date, i) => {
												const dateKey = date.toISODate() || "";
												const dailyStats = dailyStatsByRoleDate[role]?.[dateKey] ?? {
													recurring: 0,
													pickup: 0,
													overtime: 0,
													agency: 0,
												};

												const coreActual = dailyStats.recurring + dailyStats.pickup;
												const totalNeededForRole = needed.day + needed.night || 1;
												const actualNightCalc = coreActual - Math.round(coreActual * (needed.day / totalNeededForRole));

												const simNight = getSimulationCountByShift(simulationData, role, date, false);
												const totalNight = actualNightCalc + simNight;
												const missingNight = needed.night - totalNight;
												const color = getShiftColor(totalNight, needed.night);
												const isNegative = missingNight < 0;
												return (
													<StyledTableCell
														key={`missing-night-${date.toISODate()}`}
														align="center"
														onClick={() => setRecommendationAnchor({ role, date, shiftType: "night" })}
														sx={{
															borderLeft: i === 7 ? (theme) => `2px solid ${theme.palette.primary.main}` : "none",
															borderBottom: "2px solid #D1D9ED",
															color,
															fontWeight: 700,
															cursor: "pointer",
														}}
													>
														{isNegative ? (
															<Tooltip title="Surplus - consider moving staff" arrow>
																<Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
																	{missingNight}
																	<PersonRemoveOutlined sx={{ fontSize: 14, color: COLORS.warning }} />
																</Box>
															</Tooltip>
														) : (
															missingNight
														)}
													</StyledTableCell>
												);
											})}
										</StyledTableRow>
									</Fragment>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>

				<HiringMissingRecommendDialog
					open={!!recommendationAnchor}
					onClose={() => setRecommendationAnchor(null)}
					role={recommendationAnchor?.role ?? ""}
					date={recommendationAnchor?.date ?? dates[0]}
					dates={dates}
					shiftType={recommendationAnchor?.shiftType ?? null}
					calculateNeeded={calculateNeeded}
					getShiftsForDate={getShiftsForDate}
					simulationData={simulationData}
				/>
			</>
		);
	},
);

HiringStaffingTable.displayName = "HiringStaffingTable";
